import { useExportAgendaRecordsMutation, useExportAgendaRecordsZipMutation, useGetAgendaViewRecordsQuery } from 'store/services/flow/agendaRecords';
import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useGetFilterViewFormItemsQuery } from 'store/services/flow/viewFormItems';
import { useGetUsableViewQuery } from 'store/services/flow/views';
import { useGetUserFiltersQuery } from 'store/services/flow/userFilters';
import { useTranslation } from 'react-i18next';
import {
  INTERACTION_TYPE,
  PERSIST_TABLE_IDS,
  TABLE_CELL_TYPE,
  USER_AGENDA_TABLE_PRESENTATION_MODE,
  UserPermissions,
  VIEW_ITEM_STATE,
  VIEW_TYPE,
} from 'types/enums';
import { useBooleanState, useTableQueryControls, useValueFormItems, useLocaleCodeIsoNorma } from 'utils/hooks';
import { getMultipleDefinitionsUniqueNames, multipleDefinitionsToast } from 'utils/validations/multipleDefinitions';

import {
  ReactNativeMessageType,
  type AgendaRecordItem,
  type AttributeType,
  type DataSchema,
  type GetAgendaRecordsRes,
  type InlineRecordType,
  type Schema,
  type UserAgendaTableDataSchemaType,
  type UserAgendaTableProps,
} from 'types';
import { getAllUpdatedRows, getCellTypeByFormType, getFormItemType } from 'utils/agendaRecord/agendaRecord';
import { formatDisplayValue } from 'utils/agendaRecord/formatDisplayValue';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { TableRef } from 'components/common/Table/Table';
import { ControlButtonsWrapper } from 'components/common/Table/Table.styles';
import { Table } from 'components/common/Table/Table';
import { Box, FormHelperText, IconButton, Tooltip, Typography } from '@mui/material';
import { ExportButton, FilterButton } from 'components/common/Table/actions';
import { TrackedSearchField } from 'components/common/Posthog/TrackedSearchField';
import { safeTrack, trackFormButtonClicked, trackFormSubmitError, trackFormSubmitSuccess, trackOpenRecordDetail } from 'utils/posthog/metrics';
import { useDispatch, useSelector } from 'react-redux';
import { selectFilters, selectFiltersAgendaId } from 'store/entities/drawer/selectors';
import { selectGroupedTableIds } from 'store/entities/tablesQuery';
import { RecordModal } from './Records/components/RecordsItem';

import { AddRowButton } from 'components/common/Table/actions/AddRowButton';
import { Button } from 'components/mui/common/Button';
import { useFormContext } from 'react-hook-form';
import { SaveButton } from 'components/common/Table/actions/SaveButton';
import {
  useAgendaTableActions,
  useAgendaTableCalculation,
  useAgendaTableData,
  useAgendaTableValidation,
  useApplyDefaultFilters,
  useCellBlurHandler,
  useFilterCleanup,
  useFilterDataSchema,
  useFilterDrawerActions,
  useFilteredQuery,
  useFilterPaginationSync,
  useFilterVisibility,
  useGetValuesDirect,
  useMapFilterValues,
  useNormalizeFilterValues,
  useReferencedFormItems,
} from 'utils/hooks/userAgendaTable';
import { useAgendaContextMenu } from 'utils/hooks/userAgendaTable/useAgendaContextMenu';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { ContextMenuButton } from 'components/common/Table/cellComponents/ContextMenuButton';
import { updateState } from 'store/entities/navigation/actions';
import { useBreadcrumbs } from 'contexts/BreadcrumbsProvider';
import { FORM_ROLE_ITEMS, FORM_ROLE_MAIN, useFormRegistry } from 'contexts/FormRegistryProvider';
import { DEFAULT_FLOW_SUBFORM_NAMESPACE } from 'types/flow/namespace';
import { useActionBlocker } from 'contexts/ActionBlockerProvider';
import { getUUID, invokeRNMessage, isMobileApp } from 'utils/common';
import { getSelectRowId } from 'utils/table.ts';
import { FormValidationException } from 'utils/error/FormValidationException';
import { ConditionRunModal } from 'screens/user/ConditionRunModal';
import { Add, FilterList, IosShare, Save } from '@mui/icons-material';
import { MobileHeaderMenu } from 'components/common/Table/cellComponents/MobileHeaderMenu';
import { useLogin } from 'contexts';
import { MobileHeaderMenuItem } from 'components/common/Table/cellComponents/MobileHeaderMenuItem';
import { CollapsibleSearchField } from 'components/common/CollapsibleSearchField';
import { useBulkSelectAll } from 'utils/hooks/agendaTable/useBulkSelectAll';
import { useAutoSelectResultModal } from './useAutoSelectResultModal';
import { NotificationsFilterBanner } from './NotificationsFilterBanner';
import { Checkbox } from 'components/mui/common/Checkbox';
import type { ExportType } from 'components/common/Table/actions/ExportButton';
import { headerIconButtonStyles, ColoredHeader, LoadMoreButton, SpinningRefresh, TableHeaderWrapper } from './UserAgendaTable.styles';
import { SkeletonCell, SkeletonRow, TableCardWrapper, TableSkeleton } from 'components/common/Table/Table.styles';
import { buildAgendaPath } from 'utils/pathHelpers';

const SHOW_ALL_PAGE_SIZE = 9999;
const SHOW_ALL_FETCH_SIZE = 100000;
const VISIBLE_BATCH_SIZE = 50;

// TODO: move component to it's own folder and refactor it to split the code
const UserAgendaTableBase = ({
  error,
  agendaId = '',
  viewId = '',
  recordId = '',
  parentAgendaId = '',
  parentAgendaName = '',
  parentAgendaModule,
  formItemId,
  isOverview = false,
  isModule = false,
  isModal = false,
  modalOverrideCalculateValues,
  isDetail = false,
  disableRowClick = false,
  hideCheckbox = false,
  rowsCount,
  params,
  overrideParams,
  selectedRows: selectedRowsProps,
  value,
  isFetching,
  interactions,
  onValueChange,
  onSelectRow,
  presentationMode = USER_AGENDA_TABLE_PRESENTATION_MODE.AGENDA,
  isEditableColumnsAllowed = false,
  displayButtons = false,
  evaluateVisualAttributes,
  isPinnedDashboard,
  backgroundColor,
  name,
  onClick,
  formNamespace = DEFAULT_FLOW_SUBFORM_NAMESPACE,
  disableSaveButton = false,
  viewType,
  viewWidgetId,
  showAllRows,
  initialSearchTerm = '',
  isAutoSelectSingleResult,
  onAutoSelectSingleResult,
  filterRecordIds,
  onClearNotificationsFilter,
  noData,
  ...rest
}: UserAgendaTableProps) => {
  const isNestedListMode = presentationMode === USER_AGENDA_TABLE_PRESENTATION_MODE.NESTED_LIST;
  const isPortalMode = presentationMode === USER_AGENDA_TABLE_PRESENTATION_MODE.PORTAL;
  const isEmbeddedMode = isNestedListMode || isPortalMode;
  const { t } = useTranslation();
  const locale = useLocaleCodeIsoNorma();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mobileApp = isMobileApp();
  const { pathname } = useLocation();
  /**
   * The unique identifier for all tables is the generated uuid path
   * As is in src/components/common/Table/Table.tsx
   */
  const queryKey = pathname || 'default-table';
  const groupedTableIds = useSelector(selectGroupedTableIds);
  const isGroupingEnabled = groupedTableIds.includes(queryKey);

  const filterValues = useSelector(selectFilters(`userAgenda-${isOverview ? 'overview' : 'listing'}-${viewId}`));
  const [selectedRows, setSelectedRows] = useState<string[]>(selectedRowsProps ?? []);
  const tableRef = useRef<TableRef | null>(null);

  const [modal, toggleModal] = useBooleanState(false);
  const { activeRowId } = useContextMenu();
  const { insert } = useBreadcrumbs();

  const formContext = useFormContext();
  const setValue = formContext?.setValue;
  const triggerForm = formContext?.trigger;

  const { registerBlockingCondition, unregisterBlockingCondition } = useActionBlocker();
  const { register: registerForm, unregister: unregisterForm, getFormSnapshot } = useFormRegistry(formNamespace);

  const formIdentifier = useMemo(() => formItemId ?? getUUID(), [formItemId]);

  const initialSnapshotRef = useRef(isPortalMode ? undefined : getFormSnapshot(formIdentifier));
  const hydratedRef = useRef(false);

  const { data: { viewParameters = [], viewName, viewType: usableViewType, viewWidgetId: usableViewWidgetId } = {} } = useGetUsableViewQuery(
    {
      parentId: agendaId,
      viewId: viewId,
      viewParentType: 'Agenda',
    },
    { skip: !viewId }
  );

  const { data: { viewFormItems: filterViewFormItems } = {} } = useGetFilterViewFormItemsQuery(
    {
      viewId: viewId,
    },
    { skip: !viewId }
  );

  const referencedFormItemsMap = useReferencedFormItems({ filterViewFormItems });
  const filterDataSchema = useFilterDataSchema({ filterViewFormItems });
  const mapFilterValues = useMapFilterValues({ referencedFormItemsMap });

  const valueFormItems = useValueFormItems({ viewParameters, params });

  const { data: agendaData } = useGetAgendaByIdQuery({
    id: agendaId,
  });

  const isHeliosTable = useMemo(() => !!agendaData?.module?.isHeliosVisible, [agendaData]);

  const { data: savedFilters } = useGetUserFiltersQuery({
    viewId: viewId,
    agendaId: agendaId,
    pageSize: 100,
    page: 1,
    orderDesc: false,
    search: '',
    orderBy: '' as never,
  });

  const { applyDefaultFilters } = useApplyDefaultFilters({ savedFilters, viewId, isOverview });

  const { handleFilterClick } = useFilterDrawerActions({
    filterDataSchema,
    agendaId,
    viewId,
    isOverview,
    referencedFormItemsMap,
    savedFilters,
  });

  useEffect(() => {
    applyDefaultFilters();
  }, [applyDefaultFilters]);

  const [exportRecords] = useExportAgendaRecordsMutation();
  const [exportRecordsZip] = useExportAgendaRecordsZipMutation();

  const dataSchema: UserAgendaTableDataSchemaType = useMemo(() => {
    return viewParameters
      .filter(({ viewItemState }) => [VIEW_ITEM_STATE.ENABLED_AND_FILTER, VIEW_ITEM_STATE.ENABLED_AND_NO_FILTER].includes(viewItemState))
      .reduce<UserAgendaTableDataSchemaType>((acc, item, index) => {
        acc[item.parameterId ?? ''] = {
          label: item.parameterId,
          formItemDefinition: item.formItemDefinition,
          type: getCellTypeByFormType(item.formItemType!) as TABLE_CELL_TYPE,
          originalType: String(item.formItemType),
          customKey: item.parameterTitle ? String(item.parameterTitle) : String(item.parameterName),
          group: '',
          groupName: '',
          groupOrder: 0,
          dateMask: item.formItemDefinition?.date?.dateMask || '',
          isEditable: item.isEditable,
          showInToolbar: item.showInToolbar ?? false,
          order: index,
        };
        return acc;
      }, {});
  }, [viewParameters]);

  const { canShowFilters } = useFilterVisibility({ dataSchema });

  const activeFilterAgendaId = useSelector(selectFiltersAgendaId());
  const shouldPersistFilters = !isOverview && !isModal && activeFilterAgendaId && !isEmbeddedMode ? activeFilterAgendaId === agendaId : true;

  useFilterCleanup({ isOverview, viewId, shouldPersistFilters });

  const { state, setPagination, setSort, setSearch } = useTableQueryControls<typeof dataSchema, Record<string, string>>(
    `userAgenda-${isOverview ? 'overview' : 'listing'}-${viewId}`,
    {
      filters: {},
      sort: { by: '', order: 'asc', type: '' },
      search: '',
      pagination: { page: 1, pageSize: rowsCount ?? 10 },
    }
  );

  useFilterPaginationSync({
    filterValues,
    setPagination,
    rowsCount,
    currentPageSize: state?.pagination.pageSize,
  });

  const onExport = useCallback(
    async (type: ExportType) => {
      if (!selectedRows.length) return;

      try {
        await (
          type === 'Zip' || type === 'ZipWithItems'
            ? exportRecordsZip({
                recordIds: selectedRows,
                includeSubRecordMetadata: type === 'ZipWithItems',
              })
            : exportRecords({
                recordIds: selectedRows,
                exportType: type,
              })
        ).unwrap();

        toast.success(t('flow.export.sentSuccessfully'));
      } catch {
        return;
      }
    },
    [exportRecords, exportRecordsZip, selectedRows, t]
  );

  const handleSelectRows = useCallback(
    (_rowIds: string[], rows: Schema<string | number, DataSchema>[] = []) => {
      setSelectedRows(rows.map((row) => getSelectRowId(row, isHeliosTable)));
      onSelectRow?.(_rowIds, rows);
    },
    [isHeliosTable, onSelectRow]
  );

  const { filteredItems, itemsForValueRetrieval, mappedFilterValues } = useFilteredQuery({
    valueFormItems,
    filterValues,
    mapFilterValues,
  });

  const { normalizedFilters } = useNormalizeFilterValues({ mappedFilterValues });

  const transformedValueFormItems = useGetValuesDirect({
    agendaId: parentAgendaId,
    recordId,
    valueFormItems: itemsForValueRetrieval,
    isModal,
  });

  const clearVal = useMemo(() => {
    return (value ?? []).filter((val) => val != null && !val.startsWith('new'));
  }, [value]);

  const shouldSkipFetch = isNestedListMode && (!clearVal || clearVal.length === 0);
  /* TODO:
   * These conditions will need to be more complex to handle cases
   * where the valueFormItemId or valueDirect changes on an existing agenda.
   * We then need to be able still to show selected rows in the modal,
   * although they don't match the condition anymore.
   */
  const recordIdsToShow = useMemo(() => {
    if (filterRecordIds?.length) return filterRecordIds;
    return isNestedListMode ? clearVal : [];
  }, [filterRecordIds, isNestedListMode, clearVal]);

  const isShowAll = (state?.pagination.pageSize ?? 0) === SHOW_ALL_PAGE_SIZE;

  const agendaViewRecordParams = useMemo(() => {
    return {
      params: {
        agendaId,
        viewId,
        page: isShowAll ? 1 : (state?.pagination.page ?? 1),
        pageSize: isShowAll ? SHOW_ALL_FETCH_SIZE : (state?.pagination.pageSize ?? rowsCount ?? 10),
        search: state?.search ?? '',
        orderBy: state?.sort?.type ?? '',
        formItemId: state?.sort?.by ?? '',
        orderDesc: state?.sort?.order === 'desc',
      },
      evaluateVisualAttributes,
      skipViewConditions: false,
      recordIds: recordIdsToShow,
      valueFormItems: [...filteredItems, ...(overrideParams ?? transformedValueFormItems)],
      filters: normalizedFilters,
    };
  }, [
    agendaId,
    evaluateVisualAttributes,
    filteredItems,
    isShowAll,
    normalizedFilters,
    overrideParams,
    recordIdsToShow,
    rowsCount,
    state?.pagination.page,
    state?.pagination.pageSize,
    state?.search,
    state?.sort?.by,
    state?.sort?.order,
    state?.sort?.type,
    transformedValueFormItems,
    viewId,
  ]);

  const {
    data = {} as GetAgendaRecordsRes<AgendaRecordItem[]>,
    isLoading,
    isFetching: isAgendaViewFetching,
    refetch,
  } = useGetAgendaViewRecordsQuery(agendaViewRecordParams, {
    skip: !viewId || !agendaId || shouldSkipFetch,
  });

  const [visibleCount, setVisibleCount] = useState(VISIBLE_BATCH_SIZE);
  const [isLoadMorePending, setIsLoadMorePending] = useState(false);

  useEffect(() => {
    if (isShowAll) setVisibleCount(VISIBLE_BATCH_SIZE);
  }, [data?.items, isShowAll]);

  const storeNavigationParams = useCallback(
    (tableId: string) => {
      dispatch(
        updateState({
          key: `table-${tableId}`,
          data: {
            queryData: agendaViewRecordParams,
          },
        })
      );
    },
    [agendaViewRecordParams, dispatch]
  );

  const [catalogData, setCatalogData] = useState(data);

  useEffect(() => {
    if (data?.items && !catalogData?.items && (viewType ?? usableViewType) === VIEW_TYPE.CATALOGUE) {
      setCatalogData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgendaViewFetching, viewType]);

  const getTableKeys = useCallback(
    (forSort?: boolean) => {
      const types = [TABLE_CELL_TYPE.TEXT, TABLE_CELL_TYPE.USERNAME, TABLE_CELL_TYPE.EMAIL, TABLE_CELL_TYPE.DATE, TABLE_CELL_TYPE.NAME_ARRAY];
      return Object.keys(dataSchema).filter((key) => {
        const includesType = types.includes(dataSchema[key].type);
        return forSort ? !includesType : includesType;
      });
    },
    [dataSchema]
  );

  // get diff for a single row
  const getRowDiff = useCallback(
    (rowId: string): AttributeType[] | undefined => {
      const formData = tableRef.current?.getValues();
      if (!formData) return;

      const dirtyFields = tableRef.current?.getFormState().dirtyFields;
      if (!dirtyFields || !Object.keys(dirtyFields).length) {
        return [];
      }

      return getAllUpdatedRows(dirtyFields, { [rowId]: formData[rowId] }, dataSchema).get(rowId);
    },
    [dataSchema]
  );

  // get diffs for all rows (nested list) - memoized to prevent unnecessary recalculations
  const getAllRowDiffs = useCallback((): InlineRecordType[] => {
    const formData = tableRef.current?.getValues();
    if (!formData) return [];

    const dirtyFields = tableRef.current?.getFormState().dirtyFields;
    if (!dirtyFields || !Object.keys(dirtyFields).length) {
      return [];
    }

    const diffMap = getAllUpdatedRows(dirtyFields, formData, dataSchema);
    return Array.from(diffMap.entries()).map(([id, attrs]) => ({ agendaId, recordId: id, attributes: attrs }));
  }, [agendaId, dataSchema]);

  // Only use modalOverrideCalculateValues in modal context to prevent unnecessary re-renders
  // For nested lists, also use calculated values to show current form state
  const overrideValues: InlineRecordType[] | undefined = useMemo(() => {
    if (isModal) {
      return modalOverrideCalculateValues ?? [];
    }
    if (isNestedListMode && onValueChange) {
      return getAllRowDiffs();
    }
    return undefined;
  }, [isModal, modalOverrideCalculateValues, isNestedListMode, onValueChange, getAllRowDiffs]);

  const { handleCalculateRow, calculatedValues, clearCalculatedValues } = useAgendaTableCalculation({ dataSchema });

  useEffect(() => {
    clearCalculatedValues();
  }, [data, clearCalculatedValues]);

  const { transformedData, visibilityResults } = useAgendaTableData({
    data,
    shouldSkipFetch,
    overrideValues,
    calculatedValues,
  });

  const hasMoreRecords = isShowAll && (transformedData?.length ?? 0) > visibleCount;

  const handleLoadMore = useCallback(() => {
    setIsLoadMorePending(true);
    setTimeout(() => {
      setVisibleCount((prev) => prev + VISIBLE_BATCH_SIZE);
      setIsLoadMorePending(false);
    }, 0);
  }, []);

  const handleBulkSelectionChange = useCallback((ids: string[]) => {
    setSelectedRows(ids);
  }, []);

  const { isAllCurrentPageSelected, toggleSelectAll } = useBulkSelectAll({
    selectedRows,
    currentPageRows: transformedData,
    isHeliosTable,
    onSelectionChange: handleBulkSelectionChange,
  });

  useAutoSelectResultModal({
    isModal,
    isHeliosTable,
    transformedData,
    isAgendaViewFetching,
    isAutoSelectSingleResult,
    onAutoSelectSingleResult,
  });

  useEffect(() => {
    if (isModal) {
      setSearch(initialSearchTerm);
    }
  }, [initialSearchTerm, isModal, setSearch]);

  const { permissions } = useLogin();
  const resolvedViewType = viewType ?? usableViewType;
  const hasPortalCreatePermission = Boolean(
    isPortalMode && (permissions?.templateAdmin || agendaData?.userPermissions?.includes(UserPermissions.CREATION))
  );
  const showPortalAddButton = hasPortalCreatePermission && resolvedViewType !== VIEW_TYPE.ACTION_FORM && resolvedViewType !== VIEW_TYPE.SHOPPING_CART;

  useEffect(() => {
    if (!visibilityResults) {
      return;
    }

    const isTemplateAdmin = Boolean(permissions?.templateAdmin);
    if (!isTemplateAdmin) {
      return;
    }

    const multipleUiDefinitionsNames = getMultipleDefinitionsUniqueNames(visibilityResults);
    if (multipleUiDefinitionsNames.length > 0) {
      multipleDefinitionsToast(multipleUiDefinitionsNames, t);
    }
  }, [t, visibilityResults, permissions?.templateAdmin]);

  const hasEditableColumns = useMemo(
    () => !isPortalMode && !isDetail && Object.values(dataSchema).some((val) => val.isEditable),
    [dataSchema, isDetail, isPortalMode]
  );

  const { checkCalculateRequiredFields, highlightMissingFields } = useAgendaTableValidation(dataSchema);
  const { addedRows, handleOnSaveEdit, handleRowAdd, handleCancelEditRow, savingData, updateNestedList, handleRowsRemoved, isTableActionLoading } =
    useAgendaTableActions({
      agendaId,
      formItemId,
      isNestedList: isNestedListMode,
      value: value ?? [],
      dataSchema,
      setValue,
      triggerForm,
      onValueChange,
      initialAddedRows: initialSnapshotRef.current?.addedRows,
      parentRecordId: recordId,
      parentAgendaId,
    });

  const { contextMenuItems, toolbarItems, modalState, closeModal, handleConditionRunConfirm, isActionLoading } = useAgendaContextMenu({
    dataSchema,
    selectedRows,
    agendaId,
    data: (viewType ?? usableViewType) === VIEW_TYPE.CATALOGUE ? catalogData : data,
    onRemoveRecords: handleRowsRemoved,
  });
  const availableContextMenuItems = isPortalMode ? [] : contextMenuItems;
  const availableToolbarItems = isPortalMode ? [] : toolbarItems;

  const showAddRowButton = useMemo(
    () => !isPortalMode && interactions?.includes(INTERACTION_TYPE.ADDITION) && !isDetail,
    [interactions, isDetail, isPortalMode]
  );
  const showCreateRowButton = useMemo(
    () => !isPortalMode && interactions?.includes(INTERACTION_TYPE.CREATION) && !isDetail,
    [interactions, isDetail, isPortalMode]
  );
  const showSaveButton = useMemo(
    () => !isPortalMode && (hasEditableColumns || addedRows.length > 0) && !isModal && !disableSaveButton,
    [hasEditableColumns, addedRows.length, isModal, disableSaveButton, isPortalMode]
  );

  // This is important to memoize to prevent re-renders
  const modalOverrideValues = useMemo(() => (modal ? getAllRowDiffs() : []), [modal, getAllRowDiffs]);

  const handleCellBlur = useCellBlurHandler({
    dataSchema,
    agendaId,
    isNestedList: isNestedListMode,
    onValueChange,
    handleOnSaveEdit: () => handleOnSaveEdit(tableRef),
    handleCalculateRow,
    getRowDiff,
    getAllRowDiffs,
    checkCalculateRequiredFields,
    highlightMissingFields,
    tableRef,
  });

  useEffect(() => {
    if (isPortalMode || hydratedRef.current) return;
    const snap = initialSnapshotRef.current;
    if (!snap) {
      hydratedRef.current = true;
      return;
    }
    if (formItemId && Array.isArray(snap.value) && snap.value.length && Array.isArray(value) && value.length === 0) {
      updateNestedList(snap.value, formItemId, false);
    }
    if (snap.selectedRows && Array.isArray(snap.selectedRows)) {
      setSelectedRows(snap.selectedRows);
    }
    hydratedRef.current = true;
  }, [formItemId, isPortalMode, updateNestedList, value]);

  useEffect(() => {
    if (isPortalMode) return;
    registerForm(
      formIdentifier,
      {
        data: {
          formIdentifier,
          formItemId,
          agendaId,
          recordId,
          selectedRows,
          totalRows: transformedData.map((row) => row.id),
        },
        api: {
          trigger: async () => {
            if (!tableRef.current) return true;
            return tableRef.current.triggerForm();
          },
          getValues: () => tableRef.current?.getValues() ?? {},
          getDirtyFields: () => tableRef.current?.getFormState().dirtyFields ?? {},
          isDirty: () => !!tableRef.current?.getFormState().isDirty,
          getErrors: () => tableRef.current?.getFormState().errors,
          resetSavedForm: () => tableRef.current?.resetSavedForm(),
          buildSnapshot: () => {
            const snap = tableRef.current?.buildSnapshot();
            if (!snap) return;

            return {
              ...snap,
              addedRows,
              value,
              selectedRows,
              totalRows: transformedData.map((row) => row.id),
              meta: { formIdentifier, agendaId, viewId, formItemId },
            };
          },
        },
        dataSchema,
      },
      isNestedListMode ? FORM_ROLE_ITEMS : FORM_ROLE_MAIN
    );
    return () => unregisterForm(formIdentifier);
  }, [
    formItemId,
    agendaId,
    recordId,
    dataSchema,
    registerForm,
    unregisterForm,
    isNestedListMode,
    isPortalMode,
    handleOnSaveEdit,
    formIdentifier,
    viewId,
    addedRows,
    value,
    selectedRows,
    transformedData,
  ]);

  useEffect(() => {
    if (isPortalMode) return;
    const id = `dirtyForm:${formIdentifier ?? getUUID()}`;
    registerBlockingCondition('dirtyForm', {
      id,
      isBlocking: () => !!tableRef.current?.getFormState().isDirty,
      unblock: async () => {
        if (isNestedListMode) return Promise.resolve();

        safeTrack(trackFormButtonClicked, {
          button: 'save_rows',
          process: 'record',
          context: isOverview ? 'overview' : 'listing',
          area: 'save_before_leaving_modal',
        });

        try {
          await handleOnSaveEdit(tableRef);
          safeTrack(trackFormSubmitSuccess, {
            form: 'flow_listing_inline',
            process: 'record',
            step: 'save_rows_success',
            area: 'save_before_leaving_modal',
          });
        } catch (e) {
          if (!(e instanceof FormValidationException)) {
            safeTrack(trackFormSubmitError, {
              form: 'flow_listing_inline',
              process: 'record',
              step: 'save_rows_error',
              error: 'inline_error',
              area: 'save_before_leaving_modal',
            });
          }
          throw e;
        }
      },
    });
    return () => unregisterBlockingCondition(id);
  }, [
    isNestedListMode,
    isPortalMode,
    isOverview,
    formItemId,
    handleOnSaveEdit,
    registerBlockingCondition,
    unregisterBlockingCondition,
    formIdentifier,
  ]);

  useEffect(() => {
    if (isPortalMode) return;
    const id = `validForm:${formIdentifier ?? getUUID()}`;
    registerBlockingCondition('validForm', {
      id,
      isBlocking: () => !tableRef.current?.getFormState().isValid,
    });
    return () => unregisterBlockingCondition(id);
  }, [formIdentifier, formItemId, isPortalMode, registerBlockingCondition, unregisterBlockingCondition]);

  useEffect(() => {
    return () => {
      invokeRNMessage(ReactNativeMessageType.SetEditMode, { isEditMode: false });
    };
  }, []);

  const handleAddRow = useCallback(() => {
    try {
      trackFormButtonClicked({
        button: 'add_row',
        process: 'record',
        context: isOverview ? 'overview' : 'listing',
        area: isOverview ? 'overview_header' : 'listing_header',
      });
    } catch {
      /* noop */
    }
    toggleModal();
    invokeRNMessage(ReactNativeMessageType.SetEditMode, { isEditMode: true });
  }, [isOverview, toggleModal]);

  const handleCreateRow = useCallback(() => {
    try {
      trackFormButtonClicked({
        button: 'create_row',
        process: 'record',
        context: isOverview ? 'overview' : 'listing',
        area: isOverview ? 'overview_header' : 'listing_header',
      });
    } catch {
      /* noop */
    }
    handleRowAdd();
  }, [handleRowAdd, isOverview]);

  const handlePortalAdd = useCallback(() => {
    const targetModuleId = agendaData?.module.id;
    if (!targetModuleId) return;
    const targetPath = buildAgendaPath(targetModuleId, { agendaId, systemType: 'Flow' }, agendaData.module.parent?.id);
    navigate(`${targetPath}/r/new`);
  }, [agendaData?.module, agendaId, navigate]);

  const handleExport = useCallback(
    async (type: ExportType) => {
      try {
        trackFormButtonClicked({
          button: 'export',
          process: 'record',
          context: isOverview ? 'overview' : 'listing',
        });
      } catch {
        /* noop */
      }
      await onExport(type);
    },
    [onExport, isOverview]
  );

  const handleSave = useCallback(async () => {
    try {
      trackFormButtonClicked({
        button: 'save_rows',
        process: 'record',
        context: isOverview ? 'overview' : 'listing',
        area: isOverview ? 'overview_header' : 'listing_header',
      });
    } catch {
      /* noop */
    }

    try {
      await handleOnSaveEdit(tableRef);

      safeTrack(trackFormSubmitSuccess, {
        form: 'flow_listing_inline',
        process: 'record',
        step: 'save_rows_success',
        area: isOverview ? 'overview_table' : 'listing_table',
      });
    } catch (e) {
      if (e instanceof FormValidationException) return;

      safeTrack(trackFormSubmitError, {
        form: 'flow_listing_inline',
        process: 'record',
        step: 'save_rows_error',
        error: 'inline_error',
        area: isOverview ? 'overview_table' : 'listing_table',
      });
    }
  }, [handleOnSaveEdit, isOverview]);

  const handleCloseRecordModal = useCallback(() => {
    toggleModal();
    invokeRNMessage(ReactNativeMessageType.SetEditMode, { isEditMode: false });
  }, [toggleModal]);

  const handleRefresh = useCallback(() => {
    if (!shouldSkipFetch) {
      void refetch();
    }
  }, [refetch, shouldSkipFetch]);

  const shouldUseCardWrapper = !isOverview;

  const isTableLoading = isLoading || isFetching || isAgendaViewFetching;

  const tableElement = (
    <Table
      tableRef={tableRef}
      isHeliosTable={isHeliosTable}
      initialSnapshot={isPortalMode ? undefined : initialSnapshotRef.current}
      displayButtons={!isPortalMode && displayButtons}
      addRowMode={'multiple'}
      addedRows={isPortalMode ? [] : addedRows}
      page={isShowAll ? 1 : data?.page}
      isOverview={isOverview}
      data={isTableLoading ? [] : (transformedData as Schema<string, DataSchema>[])}
      visibleRowCount={isShowAll ? visibleCount : undefined}
      isLoading={isTableLoading ? false : savingData || isTableActionLoading}
      hideCheckbox={hideCheckbox || isPortalMode}
      dataSchema={dataSchema}
      rows={isTableLoading ? 0 : isShowAll ? (data?.items?.length ?? 0) : data.totalCount}
      unableSortFor={getTableKeys(true)}
      pageSize={isShowAll ? SHOW_ALL_PAGE_SIZE : (data.pageSize ?? 10)}
      hidePagination={isShowAll || isTableLoading}
      noData={
        isTableLoading ? (
          <TableSkeleton>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonRow key={i}>
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
              </SkeletonRow>
            ))}
          </TableSkeleton>
        ) : (
          noData
        )
      }
      defaultSelectedRows={isPortalMode ? [] : selectedRowsProps}
      onBlurCapture={isPortalMode ? undefined : handleCellBlur}
      onSelectRow={isPortalMode ? undefined : handleSelectRows}
      onCancelEditRow={isPortalMode ? undefined : handleCancelEditRow}
      contextMenuItems={availableContextMenuItems}
      viewType={viewType ?? usableViewType}
      viewWidgetId={viewWidgetId ?? usableViewWidgetId}
      parentId={agendaData?.module.parent?.id}
      onPageSizeChange={(pageSize) => {
        setPagination({
          page: 1,
          pageSize,
        });
      }}
      onCurrentPageChange={(page) => {
        setPagination({
          page,
          pageSize: data?.pageSize ?? 10,
        });
      }}
      onSort={(props) => {
        const { column, direction } = props;
        const type = getFormItemType(data, column as string);

        setSort({
          by: column as string,
          order: direction,
          type,
        });
      }}
      sort={{
        column: state?.sort?.by ?? 'id',
        direction: state?.sort?.order ?? 'asc',
      }}
      persistHistory
      tableId={PERSIST_TABLE_IDS.USER_AGENDA_TABLE}
      isEnableExportToCSV={!isNestedListMode && !isOverview}
      agendaId={agendaId}
      viewId={viewId}
      isColumnSizingResetEnabled
      isPinnedDashboard={isPinnedDashboard}
      showAllRows={showAllRows}
      controls={
        isOverview && !isModal ? (
          isModule ? (
            <ColoredHeader backgroundColor={isPinnedDashboard ? backgroundColor : undefined} mb={mobileApp ? '10px' : 0}>
              {name || viewName}
            </ColoredHeader>
          ) : (
            <div />
          )
        ) : (
          <TableHeaderWrapper isMobile={mobileApp}>
            {!mobileApp && (
              <>
                <TrackedSearchField
                  area={isOverview ? 'overview' : 'listing'}
                  agendaId={agendaId}
                  viewId={viewId}
                  placeholder={t('common.search.records')}
                  value={state?.search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  fullWidth
                />
              </>
            )}
            {mobileApp ? (
              <>
                {selectedRows.length > 0 && !(hideCheckbox || isPortalMode) && (
                  <Checkbox checked={isAllCurrentPageSelected} onChange={toggleSelectAll} />
                )}
                <CollapsibleSearchField
                  value={state?.search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                  placeholder={t('common.search.records')}
                />
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tooltip title={t('common.refresh')}>
                    <IconButton
                      aria-label={t('common.refresh')}
                      onClick={handleRefresh}
                      disabled={shouldSkipFetch}
                      size="medium"
                      sx={headerIconButtonStyles}>
                      <SpinningRefresh spinning={isAgendaViewFetching} fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {showSaveButton && (
                    <Tooltip title={t('common.save')}>
                      <IconButton onClick={handleSave} size="medium" sx={headerIconButtonStyles}>
                        <Save fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showAddRowButton && (
                    <Tooltip title={t('flow.modulesAndAgendas.actions.addRow')}>
                      <IconButton
                        aria-label={t('flow.modulesAndAgendas.actions.addRow')}
                        onClick={handleAddRow}
                        size="medium"
                        disabled={!!addedRows.length || isGroupingEnabled}
                        sx={{
                          ...headerIconButtonStyles,
                          bgcolor: 'rgba(39,72,251,0.06)',
                        }}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showCreateRowButton && (
                    <Tooltip title={t('flow.modulesAndAgendas.actions.createRow')}>
                      <IconButton
                        onClick={handleCreateRow}
                        size="medium"
                        disabled={isGroupingEnabled}
                        sx={{
                          ...headerIconButtonStyles,
                          bgcolor: 'rgba(39,72,251,0.06)',
                        }}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {showPortalAddButton && (
                    <Tooltip title={t('common.add')}>
                      <IconButton onClick={handlePortalAdd} size="medium" sx={headerIconButtonStyles}>
                        <Add fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <MobileHeaderMenu>
                    {canShowFilters && (
                      <MobileHeaderMenuItem
                        onClick={handleFilterClick}
                        title={t('common.filters')}
                        renderIcon={() => <FilterList />}
                        disabled={getTableKeys().length === 0 || savedFilters?.viewId !== viewId}
                      />
                    )}
                    {!isModal && !isEmbeddedMode && (
                      <MobileHeaderMenuItem
                        onClick={() => handleExport('Csv')}
                        title={t('flow.export.toCsv')}
                        renderIcon={() => <IosShare sx={{ transform: 'rotate(90deg)' }} />}
                        disabled={selectedRows?.length === 0}
                      />
                    )}
                    {!isModal && !isEmbeddedMode && (
                      <MobileHeaderMenuItem
                        onClick={() => handleExport('Pdf')}
                        title={t('flow.export.toPdf')}
                        renderIcon={() => <IosShare sx={{ transform: 'rotate(90deg)' }} />}
                        disabled={selectedRows?.length === 0}
                      />
                    )}
                    {!isModal && !isEmbeddedMode && (
                      <MobileHeaderMenuItem
                        onClick={() => handleExport('Zip')}
                        title={t('flow.export.toZipWithAttachments')}
                        renderIcon={() => <IosShare sx={{ transform: 'rotate(90deg)' }} />}
                        disabled={selectedRows?.length === 0}
                      />
                    )}
                    {!isModal && !isEmbeddedMode && (
                      <MobileHeaderMenuItem
                        onClick={() => handleExport('ZipWithItems')}
                        title={t('flow.export.toZipWithAttachmentsAndItems')}
                        renderIcon={() => <IosShare sx={{ transform: 'rotate(90deg)' }} />}
                        disabled={selectedRows?.length === 0}
                      />
                    )}
                    {!isModal && !isNestedListMode && (
                      <MobileHeaderMenuItem
                        onClick={() => tableRef.current?.exportToCSV()}
                        title={t('flow.export.allToCsv')}
                        renderIcon={() => <IosShare sx={{ transform: 'rotate(90deg)' }} />}
                        disabled={getTableKeys().length === 0}
                      />
                    )}
                    {availableContextMenuItems
                      .filter((item) => !item.showButtonDirectly)
                      .map((item, index) => (
                        <MobileHeaderMenuItem
                          key={index}
                          onClick={() => item.changeBehavior(selectedRows[0])}
                          title={item.name}
                          disabled={selectedRows.length === 0}
                        />
                      ))}
                  </MobileHeaderMenu>
                </Box>
              </>
            ) : (
              <ControlButtonsWrapper>
                <Tooltip title={t('common.refresh')}>
                  <IconButton aria-label={t('common.refresh')} onClick={handleRefresh} disabled={shouldSkipFetch} size="small">
                    <SpinningRefresh spinning={isAgendaViewFetching} />
                  </IconButton>
                </Tooltip>
                {availableToolbarItems.map((item) => (
                  <Button
                    key={item.name}
                    variant="outlined"
                    disabled={selectedRows.length === 0}
                    onClick={() => item.changeBehavior(selectedRows[0])}>
                    {item.name}
                  </Button>
                ))}
                {availableContextMenuItems.filter((item) => !item.showButtonDirectly).length > 0 && !isModal && (
                  <ContextMenuButton
                    isEditMode
                    isClickable={selectedRows?.length > 0}
                    menuItems={availableContextMenuItems
                      ?.filter((item) => !item.showButtonDirectly)
                      .map((item) => ({ label: item.name, action: item.changeBehavior }))}
                    onClick={(rowData, _, action) => {
                      if (action) {
                        action((rowData as Record<string, string>)?.id);
                      }
                    }}
                  />
                )}
                {showAddRowButton && (
                  <AddRowButton
                    aria-label={t('flow.modulesAndAgendas.actions.addRow')}
                    onClick={handleAddRow}
                    text={t('flow.modulesAndAgendas.actions.addRow')}
                    disabled={!!addedRows.length || isGroupingEnabled}
                  />
                )}
                {showCreateRowButton && (
                  <AddRowButton onClick={handleCreateRow} text={t('flow.modulesAndAgendas.actions.createRow')} disabled={isGroupingEnabled} />
                )}
                {showPortalAddButton && <AddRowButton onClick={handlePortalAdd} text={t('common.add')} />}
                {canShowFilters && (
                  <FilterButton
                    onClick={handleFilterClick}
                    disabled={getTableKeys().length === 0 || savedFilters?.viewId !== viewId}
                    activeCount={filterValues?.length}
                  />
                )}
                {!isModal && !isEmbeddedMode && <ExportButton onClick={handleExport} disabled={selectedRows?.length === 0} />}
                {showSaveButton && (
                  <SaveButton disabled={transformedData.length === 0} onClick={handleSave}>
                    {t('common.save')}
                  </SaveButton>
                )}
              </ControlButtonsWrapper>
            )}
          </TableHeaderWrapper>
        )
      }
      {...(!disableRowClick && !activeRowId
        ? {
            onEntireRowClick: (row, tableId) => {
              if (isPinnedDashboard && onClick) {
                return onClick();
              }

              storeNavigationParams(tableId);

              if (agendaData?.module) {
                const moduleId = agendaData.module.id;
                const parentId = agendaData.module.parent?.id;

                const isNavigatingToDifferentAgenda = isNestedListMode && parentAgendaId && parentAgendaId !== agendaId;

                try {
                  trackOpenRecordDetail({
                    from: isOverview ? 'overview' : 'listing',
                    agendaId,
                    viewId,
                    recordId: String(row.identifier),
                    process: 'record',
                  });
                } catch {
                  /* noop */
                }

                if (isNavigatingToDifferentAgenda) {
                  const parentModuleId = parentAgendaModule?.id || moduleId;
                  const parentParentModuleId = parentAgendaModule?.parent?.id || parentId;
                  const currentAgendaPath = parentId
                    ? `/user/modules/${parentParentModuleId}/m/${parentModuleId}/Flow/${parentAgendaId}`
                    : `/user/modules/${parentModuleId}/Flow/${parentAgendaId}`;

                  insert({
                    id: `parent-agenda-${agendaId}`,
                    label: parentAgendaName || 'Parent Agenda',
                    path: currentAgendaPath,
                  });
                }

                const detailPath = `${buildAgendaPath(moduleId, { agendaId, systemType: 'Flow' }, parentId)}/r/${row.identifier}/detail`;

                if (isPortalMode) {
                  window.open(detailPath, '_blank', 'noopener,noreferrer');
                } else {
                  navigate(detailPath);
                }
              }
            },
          }
        : {})}
      isEditableColumnsAllowed={!isPortalMode && isEditableColumnsAllowed && hasEditableColumns}
      visibilityResults={visibilityResults}
      disableRowSave={isEmbeddedMode}
      isModal={isModal}
      setCatalogData={setCatalogData}
      {...rest}
    />
  );

  return (
    Object.keys(dataSchema).length > 0 && (
      <>
        {isOverview && !isModule && (
          <ColoredHeader backgroundColor={isPinnedDashboard ? backgroundColor : undefined}>{name || viewName}</ColoredHeader>
        )}
        {!!filterRecordIds?.length && onClearNotificationsFilter && <NotificationsFilterBanner onClear={onClearNotificationsFilter} />}
        {shouldUseCardWrapper ? <TableCardWrapper isLoading={isTableLoading}>{tableElement}</TableCardWrapper> : tableElement}
        {isShowAll && (data?.totalCount ?? 0) > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              py: 2.5,
              borderTop: ({ palette }) => `1px solid ${palette.divider}`,
            }}>
            <Typography variant="body2" color="text.secondary">
              {t('common.showingRecords', {
                shown: Math.min(visibleCount, data?.items?.length ?? 0),
                total: data?.totalCount ?? 0,
              })}
            </Typography>
            {hasMoreRecords && (
              <LoadMoreButton
                variant="outlined"
                size="large"
                onClick={handleLoadMore}
                disabled={isLoadMorePending}
                startIcon={isLoadMorePending ? <SpinningRefresh spinning fontSize="small" /> : undefined}>
                {isLoadMorePending ? t('common.loading') : t('common.loadMore')}
              </LoadMoreButton>
            )}
          </Box>
        )}
        {error && error.message && (
          <FormHelperText error variant="filled">
            {error?.message}
          </FormHelperText>
        )}
        {modal && formItemId && (
          <RecordModal
            open
            agendaId={agendaId}
            recordId={recordId}
            parentAgendaId={parentAgendaId}
            viewId={viewId}
            isMultiSelect
            title={t('flow.modulesAndAgendas.form.item.records._')}
            formItemIdToShow={formItemId}
            params={params}
            selectedRows={(value ?? []).map((id) => {
              const row = transformedData.find((item) => item.id === id);
              const val = row && formItemId in row ? row[formItemId as keyof typeof row] : '';
              return {
                id,
                value: formatDisplayValue(val, isHeliosTable, locale),
              };
            })}
            onSubmit={(val) => {
              handleCloseRecordModal();
              setValue(
                formItemId,
                val.map((row) => row.id),
                { shouldDirty: true }
              );
              triggerForm(formItemId);
              onValueChange?.();
            }}
            onClose={handleCloseRecordModal}
            overrideCalculateValues={modalOverrideValues}
          />
        )}
        <ConditionRunModal
          open={modalState.open}
          onClose={closeModal}
          onConfirm={handleConditionRunConfirm}
          behaviorType={modalState.buttonBehavior}
          isSubmitting={isActionLoading}
        />
      </>
    )
  );
};

export const UserAgendaTable = memo(UserAgendaTableBase) as typeof UserAgendaTableBase;
